import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Camera, Building2, UserRound, Save, Loader2 } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { resetCompanyCache } from "@/components/BudgetSheet";
import { BrandingCard } from "@/components/BrandingCard";
import NfseSettingsCard from "@/components/NfseSettingsCard";
import BoletoSettingsCard from "@/components/BoletoSettingsCard";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const COMPANY_FIELDS = [
  ["name", "Razão social", "sm:col-span-2"],
  ["cnpj", "CNPJ"],
  ["ie", "Inscrição estadual"],
  ["address", "Endereço", "sm:col-span-2"],
  ["cep", "CEP / bairro / cidade", "sm:col-span-2"],
  ["representante", "Representante (nome e RG)", "sm:col-span-2"],
  ["whatsapp", "WhatsApp"],
  ["email", "E-mail"],
];

const Card = ({ icon: Icon, title, children, testid }) => (
  <section className="bg-white rounded-2xl border border-slate-100 card-shadow p-5" data-testid={testid}>
    <h2 className="font-heading text-base font-bold text-[#1e3a8a] mb-4 flex items-center gap-2"><Icon className="w-5 h-5" />{title}</h2>
    {children}
  </section>
);

const ProfileCard = () => {
  const { user, setUser } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const initials = user?.name?.split(" ").map((p) => p[0]).slice(0, 2).join("") || "A";

  const save = async (patch, ok) => {
    setBusy(true);
    try { const r = await api.put("/auth/me/profile", patch); setUser((u) => ({ ...u, ...r.data })); toast.success(ok); }
    catch (e) { toast.error(e?.response?.data?.detail || "Falha ao salvar"); } finally { setBusy(false); }
  };
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!f.type.startsWith("image/")) return toast.error("Escolha uma imagem");
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const up = await api.post("/upload", fd, { noOffline: true });
      await save({ picture: up.data.path }, "Foto atualizada");
    } catch { toast.error("Falha ao enviar a foto"); setBusy(false); }
  };

  return (
    <Card icon={UserRound} title="Meu perfil" testid="profile-card">
      <div className="flex items-center gap-4">
        <button type="button" onClick={() => fileRef.current?.click()} disabled={busy} className="relative group shrink-0" aria-label="Trocar foto" data-testid="profile-photo-btn">
          <Avatar className="w-20 h-20 border-2 border-white shadow">
            <AvatarImage src={fileUrl(user?.picture)} alt={user?.name} className="object-cover" />
            <AvatarFallback className="bg-[#1e3a8a] text-white text-xl">{initials}</AvatarFallback>
          </Avatar>
          <span className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-[#7c3aed] text-white flex items-center justify-center shadow group-hover:scale-110 transition-transform">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
          </span>
        </button>
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="profile-photo-input" />
        <div className="min-w-0 flex-1 space-y-2">
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Seu nome" data-testid="profile-name" />
          <p className="text-xs text-slate-500 truncate">{user?.email}</p>
          <div className="flex gap-2">
            <Button size="sm" disabled={busy || !name.trim() || name === user?.name} onClick={() => save({ name }, "Nome atualizado")} data-testid="profile-name-save">Salvar nome</Button>
            {user?.picture && <Button size="sm" variant="outline" disabled={busy} onClick={() => save({ picture: "" }, "Foto removida")} data-testid="profile-photo-remove">Tirar foto</Button>}
          </div>
        </div>
      </div>
    </Card>
  );
};

const CompanyCard = () => {
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/company-profile").then((r) => setF(r.data)).catch(() => {}); }, []);
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try { const r = await api.put("/company-profile", f); setF(r.data); resetCompanyCache(); toast.success("Dados da empresa salvos — já valem nos orçamentos"); }
    catch (err) { toast.error(err?.response?.data?.detail || "Falha ao salvar"); } finally { setSaving(false); }
  };
  return (
    <Card icon={Building2} title="Dados da empresa" testid="company-card">
      <p className="text-xs text-slate-500 -mt-2 mb-4">Esses dados saem no cabeçalho dos orçamentos, nos PDFs e nas declarações.</p>
      {!f ? <Loader2 className="w-5 h-5 animate-spin text-slate-400" /> : (
        <form onSubmit={submit} className="grid sm:grid-cols-2 gap-3">
          {COMPANY_FIELDS.map(([k, label, span]) => (
            <label key={k} className={`block ${span || ""}`}>
              <span className="block text-[11px] font-bold text-slate-500 mb-1">{label}</span>
              <Input value={f[k] || ""} onChange={(e) => setF((p) => ({ ...p, [k]: e.target.value }))} data-testid={`company-${k}`} />
            </label>
          ))}
          <Button type="submit" disabled={saving} className="sm:col-span-2 bg-[#1e3a8a] hover:bg-[#1e40af] text-white" data-testid="company-save">
            <Save className="w-4 h-4 mr-2" />{saving ? "Salvando…" : "Salvar dados da empresa"}
          </Button>
        </form>
      )}
    </Card>
  );
};

export default function MinhaConta() {
  return (
    <div className="space-y-4 max-w-3xl" data-testid="minha-conta-page">
      <h1 className="font-heading text-2xl font-black text-slate-900">Minha conta</h1>
      <ProfileCard />
      <BrandingCard />
      <NfseSettingsCard />
      <BoletoSettingsCard />
      <CompanyCard />
    </div>
  );
}
